import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { Button, Col, Modal, Row } from 'antd';
import { Form } from 'formik-antd';
import * as Yup from 'yup';
import cx from 'classnames';

import {
  disableDatesBeforeSpecificDate,
  disablePastDates,
  getDateWithoutTime,
  getLaterDate,
} from 'utils/dateUtils';
import {
  getAssignmentTerminationModel,
  getRenewInfoForAssignment,
  renewAssignmentWithoutChanges,
} from 'redux/reducers/assignmentSlice';
import FormItem from 'components/formik/FormItem';
import DatePicker from 'components/formik/DatePicker';
import BaseAlert from 'components/wrappers/BaseAlert';
import SubmitButton from 'components/formik/SubmitButton';
import RenewAssignmentModal from './RenewAssignmentModal';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  AssignmentEditAdminResponseModel,
  BreakInServiceEnum,
} from 'api/adminApiClient';
import { validateNoEndDateLessStartDate } from 'utils/validationUtils';
import {
  AssignmentEditUserResponseModel,
  AssignmentTerminationModel,
  BOUsersPriceAgreementRespModel,
  CustomerResponseModel,
  RenewAssignmentModel,
  RenewKindEnum,
} from 'api/backofficeApiClient';
import {
  isAdminSelector,
  isCorporateTASelector,
} from 'redux/selectors/authenticationSelectors';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getMemberPriceAgreement } from 'redux/reducers/recruiterSlice';
import { getPricingAgreement } from 'redux/reducers/accountSlice';
import {
  getApprovedClientsByMember,
  getMyApprovedClients,
} from 'redux/reducers/clientSlice';
import { getCustomerBreakInServiceValue } from 'utils/clientUtils';
import { isTerminationDateInFuture } from 'utils/assignmentUtils';

import styles from './RenewalTypeModal.module.less';

type Props = {
  assignmentId: string;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  breakInServiceEnumValue?: BreakInServiceEnum;
  companyId?: string;
};

interface AssignmentRenewalFormValues {
  startDate: string | null;
  endDate: string | null;
}

enum RenewalTypeEnum {
  WithChanges = 1,
  WithoutChanges = 2,
}

const getValidationSchema = (hasActiveScheduledTerminationInFuture?: boolean) =>
  Yup.object().shape(
    {
      startDate: Yup.date()
        .nullable()
        .required('Field is required')
        .when('endDate', {
          is: () => hasActiveScheduledTerminationInFuture,
          then: Yup.date().min(
            new Date(new Date().setHours(0, 0, 0, 0)),
            'Start date should not be in the past',
          ),
        }),
      endDate: Yup.date()
        .nullable()
        .required('Field is required')
        .when('startDate', {
          is: (startDate?: Date | null) => !!startDate,
          then: Yup.date()
            .nullable()
            .test(
              'isEndDateLessThanStartDate',
              'End date cannot be less than start date',
              function (endDate?: Date | null) {
                if (!endDate) return true;

                return validateNoEndDateLessStartDate(
                  this.parent.startDate,
                  endDate,
                );
              },
            ),
        }),
    },
    [['endDate', 'startDate']],
  );

export default function RenewalTypeModal(props: Props) {
  const {
    onClose,
    assignmentId,
    onSubmit,
    breakInServiceEnumValue,
    companyId,
  } = props;
  const formRef: any = useRef();
  const dispatch = useAppDispatch();
  const isAdminRole = useAppSelector(isAdminSelector);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const [activeRenewalType, setActiveRenewalType] = useState<RenewalTypeEnum>();
  const [isRenewSaving, setIsRenewSaving] = useState(false);
  const [dateFieldsEnabled, setDateFieldsEnabled] = useState(false);
  const [isDataForRenewLoading, setIsDataForRenewLoading] = useState(false);
  const [isRenewAssignmentModalVisible, setIsRenewAssignmentModalVisible] =
    useState(false);
  const [renewAssignmentInfo, setRenewAssignmentInfo] = useState<
    AssignmentEditUserResponseModel | AssignmentEditAdminResponseModel
  >();
  const [priceAgreement, setPriceAgreement] =
    useState<BOUsersPriceAgreementRespModel>();
  const [customers, setCustomers] = useState<CustomerResponseModel[]>([]);
  const [isCustomersLoaded, setIsCustomersLoaded] = useState(false);
  const [assignmentTerminationDetails, setAssignmentTerminationDetails] =
    useState<AssignmentTerminationModel>();
  const hasActiveScheduledTerminationInFuture = isTerminationDateInFuture(
    assignmentTerminationDetails?.terminationDate,
  );
  const breakInServiceValue = getCustomerBreakInServiceValue(
    breakInServiceEnumValue,
  );

  const loadAssignmentTermination = useCallback(async () => {
    if (!assignmentId) return;
    const result = await dispatch(
      getAssignmentTerminationModel({
        assignmentId: assignmentId,
        isAdmin: isAdminRole,
      }),
    );

    if (getAssignmentTerminationModel.fulfilled.match(result)) {
      setAssignmentTerminationDetails(result.payload);
    } else {
      setAssignmentTerminationDetails(undefined);
    }
  }, [dispatch, isAdminRole, assignmentId]);

  useEffect(() => {
    loadAssignmentTermination();
  }, [loadAssignmentTermination]);

  const loadPriceAgreement = async () => {
    const correctAction: any = isAdminRole
      ? getMemberPriceAgreement
      : getPricingAgreement;

    const result = await dispatch(
      correctAction(isAdminRole ? companyId : undefined),
    );

    if (correctAction.fulfilled.match(result)) {
      setPriceAgreement(result.payload);
    }
  };

  const loadCustomers = async () => {
    if (isCorporateTA) return;

    const correctAction: any = isAdminRole
      ? getApprovedClientsByMember
      : getMyApprovedClients;

    const result = await dispatch(
      correctAction(isAdminRole ? companyId : undefined),
    );

    if (correctAction.fulfilled.match(result)) {
      setCustomers(result.payload);
    }
    setIsCustomersLoaded(true);
  };

  useEffect(() => {
    loadPriceAgreement();
    loadCustomers();
    // eslint-disable-next-line
  }, []);

  const initialValues: AssignmentRenewalFormValues = {
    startDate: null,
    endDate: null,
  };

  const handleOpenRenewAssignmentModal = () => {
    setIsRenewAssignmentModalVisible(true);
  };

  const handleCloseRenewAssignmentModal = () => {
    onClose();
    setIsRenewAssignmentModalVisible(false);
  };

  const displayDateFields = (
    formikHelpers: FormikHelpers<AssignmentRenewalFormValues>,
  ) => {
    formikHelpers.setFieldTouched('startDate', false, false);
    formikHelpers.setFieldTouched('endDate', false, false);

    setDateFieldsEnabled(true);
  };

  const handleSubmit = async (
    values: AssignmentRenewalFormValues,
    formikHelpers: FormikHelpers<AssignmentRenewalFormValues>,
  ) => {
    if (activeRenewalType === RenewalTypeEnum.WithChanges)
      return handleSubmitWithChanges();

    if (!dateFieldsEnabled) {
      return displayDateFields(formikHelpers);
    }

    const { startDate, endDate } = values;

    if (!startDate || !endDate) return;

    const data: RenewAssignmentModel = {
      jobStartDate: getDateWithoutTime(startDate),
      jobEndDate: getDateWithoutTime(endDate),
      parentAssignmentId: assignmentId,
      renewKind: RenewKindEnum.Renew,
    };

    setIsRenewSaving(true);
    const result = await dispatch(
      renewAssignmentWithoutChanges({
        data,
        isAdminRole: isAdminRole as boolean,
      }),
    );
    setIsRenewSaving(false);

    if (renewAssignmentWithoutChanges.fulfilled.match(result)) {
      BaseAlert.success(
        'Renewal request submitted. Upon approval your changes will become visible here.',
      );
      onClose();

      await onSubmit();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleSubmitWithChanges = async () => {
    setIsDataForRenewLoading(true);
    const result = await dispatch(
      getRenewInfoForAssignment({
        assignmentId,
        isAdminRole: isAdminRole as boolean,
      }),
    );

    if (getRenewInfoForAssignment.fulfilled.match(result)) {
      setRenewAssignmentInfo(result.payload);
      handleOpenRenewAssignmentModal();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
    setIsDataForRenewLoading(false);
  };

  const handleCloseModal = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current.dirty) {
      return Modal.confirm({
        title: 'Changes won’t be saved. Are you sure?',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  return (
    <>
      {isRenewAssignmentModalVisible && renewAssignmentInfo && (
        <RenewAssignmentModal
          onSubmit={onSubmit}
          onClose={handleCloseRenewAssignmentModal}
          renewAssignmentInfo={renewAssignmentInfo}
          priceAgreement={priceAgreement}
          customers={customers}
          isCustomersLoaded={isCustomersLoaded}
          hasActiveScheduledTerminationInFuture={
            hasActiveScheduledTerminationInFuture
          }
        />
      )}

      <BaseModal
        title="Select renewal type"
        open={true}
        onCancel={handleCloseModal}
        destroyOnClose
        maxWidth={570}
      >
        <Formik
          innerRef={formRef}
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={
            dateFieldsEnabled
              ? getValidationSchema(hasActiveScheduledTerminationInFuture)
              : null
          }
        >
          {(formik) => (
            <Form layout="vertical">
              <div className={cx('modal-scroll-wrapper', styles.renewalModal)}>
                <div className={styles.renewalText}>
                  Select the action below
                </div>

                {dateFieldsEnabled && (
                  <Row gutter={[20, 20]}>
                    <Col xs={24} md={12}>
                      <FormItem label="Start date *" name="startDate">
                        <DatePicker
                          name="startDate"
                          placeholder="Select start date"
                          disabledDate={
                            hasActiveScheduledTerminationInFuture
                              ? disablePastDates
                              : undefined
                          }
                          showToday={false}
                          format="MM/DD/YYYY"
                          className="d-flex"
                        />
                      </FormItem>
                    </Col>
                    <Col xs={24} md={12}>
                      <FormItem label="End date (expected) *" name="endDate">
                        <DatePicker
                          key={formik.values.startDate || 'endDate'}
                          name="endDate"
                          placeholder="Select end date"
                          disabledDate={(current) =>
                            hasActiveScheduledTerminationInFuture
                              ? disableDatesBeforeSpecificDate(
                                  getLaterDate(
                                    formik.values.startDate,
                                    breakInServiceValue
                                      ? breakInServiceValue + 1
                                      : undefined,
                                  ),
                                )(current)
                              : disableDatesBeforeSpecificDate(
                                  formik.values.startDate,
                                )(current)
                          }
                          showToday={false}
                          format="MM/DD/YYYY"
                          className="d-flex"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                )}

                {!dateFieldsEnabled && (
                  <Row gutter={[20, 20]}>
                    <Col xs={24} md={12}>
                      <div
                        className={cx(styles.card, {
                          [styles.isActive]:
                            activeRenewalType === RenewalTypeEnum.WithChanges,
                        })}
                      >
                        <Button
                          onClick={() =>
                            setActiveRenewalType(RenewalTypeEnum.WithChanges)
                          }
                        >
                          Submit with changes
                        </Button>
                      </div>
                    </Col>

                    <Col xs={24} md={12}>
                      <div
                        className={cx(styles.card, {
                          [styles.isActive]:
                            activeRenewalType ===
                            RenewalTypeEnum.WithoutChanges,
                        })}
                      >
                        <Button
                          onClick={() =>
                            setActiveRenewalType(RenewalTypeEnum.WithoutChanges)
                          }
                        >
                          Submit without changes{' '}
                          <span className="d-sm-none">(dates only)</span>
                        </Button>
                      </div>
                    </Col>
                  </Row>
                )}
              </div>

              <BaseModalFooter
                className={cx('button-space-between', styles.renewalFooter)}
              >
                <Button
                  className="modal-icon-btn d-xs-none"
                  onClick={handleCloseModal}
                >
                  <span className="icon icon-closed-outlined" />
                  Cancel
                </Button>

                <SubmitButton
                  type="primary"
                  disabled={
                    isDataForRenewLoading ||
                    formik.isSubmitting ||
                    !activeRenewalType ||
                    isRenewSaving
                  }
                >
                  Next
                </SubmitButton>
              </BaseModalFooter>
            </Form>
          )}
        </Formik>
      </BaseModal>
    </>
  );
}
